import type { ConnectPaymentProviderBody } from '@restaurant/api-client';
import {
  ConnectPaymentProviderInputSchema,
  type OnlinePaymentProvider,
  type PaymentProviderConfig,
  type PaymentProviderMode,
} from '@restaurant/api-contracts';
import { z } from 'zod';

export interface ConnectProviderValues {
  mode: PaymentProviderMode;
  activate: boolean | null;
  credentials: {
    provider: OnlinePaymentProvider;
    clientId: string;
    clientSecret: string;
    baseUrl: string;
    tenantId: string;
    tenantSecret: string;
  };
}

export const connectDefaults = (
  provider: OnlinePaymentProvider,
  mode: PaymentProviderMode,
): ConnectProviderValues => ({
  mode,
  activate: null,
  credentials: {
    provider,
    clientId: '',
    clientSecret: '',
    baseUrl: '',
    tenantId: '',
    tenantSecret: '',
  },
});

export const savedActivation = (
  providers: PaymentProviderConfig[],
  provider: OnlinePaymentProvider,
  mode: PaymentProviderMode,
): boolean => {
  const saved = providers.find((p) => p.provider === provider && p.mode === mode);
  return saved ? saved.isActive : !providers.some((p) => p.isActive);
};

export const hasTypedCredentials = ({
  provider,
  clientId,
  clientSecret,
  baseUrl,
  tenantId,
  tenantSecret,
}: ConnectProviderValues['credentials']): boolean =>
  provider === 'PAYPAL'
    ? Boolean(clientId.trim() || clientSecret.trim())
    : Boolean(baseUrl.trim() || tenantId.trim() || tenantSecret.trim());

export function connectBody(
  values: ConnectProviderValues,
  providers: PaymentProviderConfig[],
): ConnectPaymentProviderBody {
  const { provider, clientId, clientSecret, baseUrl, tenantId, tenantSecret } = values.credentials;
  return {
    mode: values.mode,
    activate: values.activate ?? savedActivation(providers, provider, values.mode),
    credentials:
      provider === 'PAYPAL'
        ? { provider, clientId: clientId.trim(), clientSecret: clientSecret.trim() }
        : {
            provider,
            baseUrl: baseUrl.trim(),
            tenantId: tenantId.trim(),
            tenantSecret: tenantSecret.trim(),
          },
  };
}

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export function connectSchema(text: {
  noWhitespace: string;
  clientIdEmail: string;
  invalidUrl: string;
}) {
  const formatChecks = z
    .any()
    .superRefine((data: Record<string, unknown>, ctx: z.RefinementCtx) => {
      const creds = data?.credentials as Record<string, string> | undefined;
      if (!creds) return;
      const str = (k: string) => (typeof creds[k] === 'string' ? creds[k] : '');
      const flag = (field: string, message: string) =>
        ctx.addIssue({ code: 'custom', path: ['credentials', field], message });
      if (creds.provider === 'PAYPAL') {
        if (str('clientId') && str('clientId').includes('@')) flag('clientId', text.clientIdEmail);
        else if (str('clientId') && /\s/.test(str('clientId'))) flag('clientId', text.noWhitespace);
        if (str('clientSecret') && /\s/.test(str('clientSecret')))
          flag('clientSecret', text.noWhitespace);
      } else if (str('baseUrl') && isValidUrl(str('baseUrl')) && !isHttpsUrl(str('baseUrl'))) {
        flag('baseUrl', text.invalidUrl);
      }
    });

  return ConnectPaymentProviderInputSchema.and(formatChecks);
}
